import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AuthProvider } from './context/AuthContext';
import { useState, useEffect } from 'react';

// Theme
import theme from './theme';

// Layouts
import DesktopLayout from './layouts/DesktopLayout';
import MobileLayout from './layouts/MobileLayout';

// Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import NewRequest from './pages/NewRequest';
import RequestsList from './pages/RequestsList';
import ReviewRequests from './pages/ReviewRequests';
import Profile from './pages/Profile';
import Notifications from './pages/Notifications';
import StudentDetails from './pages/StudentDetails';
import TeacherStudents from './pages/TeacherStudents';
import CoordinatorStudents from './pages/CoordinatorStudents';
import ResetPassword from './pages/ResetPassword';
import UpdatePassword from './pages/UpdatePassword';
import Benefits from './pages/Benefits';
import Suggestions from './pages/Suggestions';
import ClassManagement from './pages/ClassManagement';
import BenefitsManagement from './pages/BenefitsManagement';
import AuditLog from './pages/AuditLog';
import Guide from './pages/Guide';
import ProfileIntegrations from './pages/ProfileIntegrations';
import CoordinatorIntegrations from './pages/CoordinatorIntegrations';
import AuditIntegrations from './pages/AuditIntegrations';
import StatisticsClasses from './pages/StatisticsClasses';
import StatisticsStudent from './pages/StatisticsStudent';
import CoordinatorRequests from './pages/CoordinatorRequests';
import StatisticsSuggestions from './pages/StatisticsSuggestions';
import IntelligenceDebug from './pages/IntelligenceDebug';
import Intelligence from './pages/Intelligence';

// Components
import PrivateRoute from './components/PrivateRoute';

function AppContent() {
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const Layout = isMobile ? MobileLayout : DesktopLayout;

  return (
    <Router>
      <Routes>
        {/* Ruta login - publică */}
        <Route path="/login" element={<Login />} />
        
        {/* Redirect de la root la login sau dashboard */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        
        {/* Ruta pentru redirect root după autentificare */}
        <Route path="/app" element={<Navigate to="/dashboard" replace />} />

        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/update-password" element={<UpdatePassword />} />

        {/* Rute protejate - fiecare cu Layout propriu */}
        <Route path="/dashboard" element={
          <PrivateRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/profile" element={
          <PrivateRoute>
            <Layout>
              <Profile />
            </Layout>
          </PrivateRoute>
        } />

        <Route path="/profil/integrari" element={
          <PrivateRoute roles={['elev']}>
            <Layout>
              <ProfileIntegrations />
            </Layout>
          </PrivateRoute>
        } />

        <Route path="/intelligence" element={
          <PrivateRoute roles={['profesor', 'coordonator']}>
            <Layout>
              <Intelligence />
            </Layout>
          </PrivateRoute>
        } />

        <Route path="/statistici" element={<Navigate to="/intelligence" replace />} />
        <Route path="/statistici/anterior" element={<Navigate to="/intelligence" replace />} />
        <Route path="/statistici/elevi" element={<Navigate to="/intelligence" replace />} />

        <Route path="/statistici/clase" element={
          <PrivateRoute roles={['profesor', 'coordonator']}>
            <Layout>
              <StatisticsClasses />
            </Layout>
          </PrivateRoute>
        } />

        <Route path="/statistici/sugestii" element={
          <PrivateRoute roles={['coordonator']}>
            <Layout>
              <StatisticsSuggestions />
            </Layout>
          </PrivateRoute>
        } />

        <Route path="/intelligence/debug" element={
          <PrivateRoute roles={['profesor', 'coordonator']}>
            <Layout>
              <IntelligenceDebug />
            </Layout>
          </PrivateRoute>
        } />

        <Route path="/statistici/elev/:id" element={
          <PrivateRoute roles={['profesor', 'coordonator']}>
            <Layout>
              <StatisticsStudent />
            </Layout>
          </PrivateRoute>
        } />

        <Route path="/coordonator/cereri" element={
          <PrivateRoute roles={['coordonator']}>
            <Layout>
              <CoordinatorRequests />
            </Layout>
          </PrivateRoute>
        } />

        <Route path="/coordonator/sugestii" element={<Navigate to="/intelligence" replace />} />

        <Route path="/coordonator/integrari" element={
          <PrivateRoute roles={['coordonator']}>
            <Layout>
              <CoordinatorIntegrations />
            </Layout>
          </PrivateRoute>
        } />

        <Route path="/coordonator/audit-integrari" element={
          <PrivateRoute roles={['coordonator']}>
            <Layout>
              <AuditIntegrations />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/notifications" element={
          <PrivateRoute>
            <Layout>
              <Notifications />
            </Layout>
          </PrivateRoute>
        } />

        <Route path="/guide" element={
          <PrivateRoute>
            <Layout>
              <Guide />
            </Layout>
          </PrivateRoute>
        } />
        
        {/* Rute pentru ELEVI */}
        <Route path="/new-request" element={
          <PrivateRoute roles={['elev']}>
            <Layout>
              <NewRequest />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/my-requests" element={
          <PrivateRoute roles={['elev']}>
            <Layout>
              <RequestsList />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/benefits" element={
          <PrivateRoute roles={['elev']}>
            <Layout>
              <Benefits />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/suggestions" element={
          <PrivateRoute roles={['elev']}>
            <Layout>
              <Suggestions />
            </Layout>
          </PrivateRoute>
        } />
        
        {/* Rute pentru PROFESORI */}
        <Route path="/review-requests" element={
          <PrivateRoute roles={['profesor', 'coordonator']}>
            <Layout>
              <ReviewRequests />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/teacher-students" element={
          <PrivateRoute roles={['profesor']}>
            <Layout>
              <TeacherStudents />
            </Layout>
          </PrivateRoute>
        } />
        
        {/* Rute pentru COORDONATORI */}
        <Route path="/overview" element={<Navigate to="/intelligence" replace />} />
        
        <Route path="/coordinator-students" element={
          <PrivateRoute roles={['coordonator']}>
            <Layout>
              <CoordinatorStudents />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/class-management" element={
          <PrivateRoute roles={['coordonator']}>
            <Layout>
              <ClassManagement />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/audit-log" element={
          <PrivateRoute roles={['coordonator']}>
            <Layout>
              <AuditLog />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/benefits-management" element={
          <PrivateRoute roles={['coordonator']}>
            <Layout>
              <BenefitsManagement />
            </Layout>
          </PrivateRoute>
        } />
        
        {/* Rute pentru PROFESORI și COORDONATORI */}
        <Route path="/student/:id" element={
          <PrivateRoute roles={['profesor', 'coordonator']}>
            <Layout>
              <StudentDetails />
            </Layout>
          </PrivateRoute>
        } />
        
        {/* Rute speciale pentru Layout-uri */}
        <Route path="/review" element={
          <PrivateRoute roles={['profesor', 'coordonator']}>
            <Layout>
              <ReviewRequests />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/teacher/students" element={
          <PrivateRoute roles={['profesor']}>
            <Layout>
              <TeacherStudents />
            </Layout>
          </PrivateRoute>
        } />
        
        <Route path="/coordinator/students" element={
          <PrivateRoute roles={['coordonator']}>
            <Layout>
              <CoordinatorStudents />
            </Layout>
          </PrivateRoute>
        } />
        
        {/* Ruta fallback - pentru orice altă rută */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
  );
}

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f8fafc',
        fontFamily: 'Inter, sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #E2E8F0',
            borderTopColor: '#4F46E5',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}>
          </div>
          <div style={{
            fontSize: '18px',
            color: '#000000',
            fontWeight: '500'
          }}>
            Se încarcă Sistemul de Credite...
          </div>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;